import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { NewBadge, Tooltip } from '../components/ui';
import { useNewFeature } from './useNewFeature';
import { useWhatsNew } from './WhatsNewContext';
import { track } from '../lib/track';

interface NewFeatureProps {
  /** Key of the entry in `releases.ts`. */
  featureKey: string;
  children: ReactNode;
  /** `pill` adds « NEW » after the children ; `dot` a small marker on a relative parent ; `none` tooltip only. */
  badge?: 'pill' | 'dot' | 'none';
  /** What the tooltip says once the feature is no longer new (default: the release item text). */
  tooltip?: ReactNode;
  side?: 'top' | 'bottom' | 'right';
  className?: string;
}

/**
 * Wraps a control that ships a recent feature: a badge flags it for the first weeks and a
 * short tooltip names it, with a link to the full « Nouveautés » panel. Once the feature is
 * no longer new, the tooltip explains the control. See `useNewFeature` for the lifetime rules.
 */
export function NewFeature({ featureKey, children, badge = 'pill', tooltip, side, className = '' }: NewFeatureProps) {
  const { isNew, item } = useNewFeature(featureKey);
  const { openWhatsNew } = useWhatsNew();

  const content = isNew && item ? (
    <span className="block space-y-1">
      <span className="block font-semibold">{item.title}</span>
      <button
        type="button"
        onClick={() => {
          track('new-badge', 'click', undefined, { feature_key: featureKey });
          openWhatsNew('tooltip');
        }}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary-200 hover:text-white"
      >
        Toutes les nouveautés <ArrowRight className="h-3 w-3" />
      </button>
    </span>
  ) : (
    tooltip ?? item?.text ?? null
  );

  return (
    <Tooltip content={content} side={side} className={`items-center gap-1.5 ${badge === 'dot' ? 'relative' : ''} ${className}`}>
      {children}
      {isNew && badge === 'pill' && <NewBadge />}
      {isNew && badge === 'dot' && <NewBadge variant="dot" />}
    </Tooltip>
  );
}
